import { FunctionComponent, ReactNode } from "react";
import { LucideIcon, WalletCards, Clock } from "lucide-react";
import { formatCurrencyBRL } from "../../../utils/format";
import SkeletonLine from "../../skeleton/SkeletonLine";
import "./HeroBalance.scss";

/**
 * Faixa de tamanho do saldo, recalculada a cada mudanca de valor.
 *
 * O saldo e monoespacado, entao cada digito a mais empurra a largura de forma
 * previsivel — e no celular a linha nao tem para onde crescer. Abaixo de 100 o
 * corpo cheio cabe; das centenas em diante ele precisa encolher, e a partir do
 * milhar encolher de novo. Quem aplica os tamanhos e o CSS, so no mobile: no
 * desktop a largura sobra e o corpo de origem fica.
 */
const faixaDoSaldo = (valor: number): "lg" | "md" | "sm" => {
    const grandeza = Math.abs(valor);
    if (grandeza >= 1_000) return "sm";
    if (grandeza >= 100)   return "md";
    return "lg";
};

interface HeroBalanceProps {
    /** Icone do label (default WalletCards) */
    icon?: LucideIcon;
    /** Label superior pequeno (uppercase) */
    label: ReactNode;
    /** Saldo principal — numero ou null/undefined p/ mostrar skeleton */
    balance: number | null | undefined;
    /** Loading explicito (sobrescreve a deteccao por balance==undefined) */
    loading?: boolean;
    /** Valor pendente (mostra pill amarelo) */
    pending?: number;
    /** Texto da ultima transacao */
    lastTx?: string;
    /** Botoes do lado direito */
    actions?: ReactNode;
    /** Classe extra (ex: "personal") */
    className?: string;
}

const HeroBalance: FunctionComponent<HeroBalanceProps> = ({
    icon: Icon = WalletCards,
    label,
    balance,
    loading,
    pending,
    lastTx,
    actions,
    className = "",
}) => {
    const isLoading = loading ?? (balance === undefined || balance === null);

    return (
        <section className={`page-card ui-hero-balance ${className}`.trim()}>
            <div className="hb-content">
                <div className="hb-text">
                    <span className="hb-label">
                        <Icon size={14} /> {label}
                    </span>

                    <strong
                        className="hb-value num"
                        data-size={isLoading ? undefined : faixaDoSaldo(Number(balance ?? 0))}
                    >
                        {isLoading
                            ? <SkeletonLine height="44px" />
                            : formatCurrencyBRL(Number(balance ?? 0))}
                    </strong>

                    {(pending !== undefined || lastTx) && (
                        <div className="hb-meta">
                            {pending !== undefined && (
                                <span className="hb-pill warn">
                                    <Clock size={12} /> pendente
                                    <b className="num">{formatCurrencyBRL(pending)}</b>
                                </span>
                            )}
                            {lastTx && (
                                <span className="hb-pill neutral">
                                    ultima transacao <b className="num">{lastTx}</b>
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {actions && <div className="hb-actions">{actions}</div>}
            </div>
        </section>
    );
};

export default HeroBalance;
